import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { AxiosError } from 'axios';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RunTransition, WorkflowService } from 'src/app/shared/services/workflow/workflow.service';
import { FullPaymentWarningComponent } from '../full-payment-warning/full-payment-warning.component';

export type DialogData = {
  transition_ids: {
    full_payment: string,
    partial_payment: string,
  },
  claims: {
    is_paid: boolean;
    remaining_amount: number,
    currency_iso: string;
    claim_type_label: string,
    optional_claims: {
      remaining_amount: number,
      claim_type_label: string,
    }[];
  }[];
};

export type TransitionParam = {
  amount: number;
};

type PaymentAmount = {
  amount: number;
  optional_claims: {
    amount: number;
  }[];
};

@Component({
  selector: 'app-full-payment-warning',
  templateUrl: './manual-payment.component.html',
  styleUrls: ['./manual-payment.component.scss']
})
export class ManualPaymentComponent implements OnInit, OnDestroy {
  dialogData!: DialogData;
  loading = false;

  currencyOptions: string[] = [];

  allSums: number = Infinity;
  get isFullPayment(): boolean { return this.fullPayment.value || this.paymentAmount.value >= this.allSums; }

  readonly paymentAmount = new FormControl(null);
  readonly currency = new FormControl(null);
  readonly fullPayment = new FormControl(false);
  readonly manualPaymentForm = new FormGroup({
    paymentAmount: this.paymentAmount,
    currency: this.currency,
    fullPayment: this.fullPayment,
  });
  private readonly destory = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<ManualPaymentComponent>,
    @Inject(MAT_DIALOG_DATA) private data: RunTransition,
    private workflowService: WorkflowService,
    private snackbar: MatSnackBar,
    private translate: TranslateService,
  ) {
  }

  async ngOnInit(): Promise<void> {
    await this.setDialogData();

    this.currencyOptions = Array.from(new Set(this.dialogData.claims
      .filter(c => !c.is_paid)
      .map(c => c.currency_iso)));

    this.currency.valueChanges
      .pipe(takeUntil(this.destory))
      .subscribe(currency => {
        const validators = [
          this.numberValidator,
          Validators.required,
          Validators.min(1),
        ];

        if (currency) {
          const sum = this.dialogData.claims
            .filter(c => c.currency_iso === currency)
            .reduce((s, c) => s + c.remaining_amount, 0);
          validators.push(Validators.max(sum));
        }

        this.paymentAmount.setValidators(validators);
        this.paymentAmount.updateValueAndValidity();
      });

    this.fullPayment.valueChanges
      .pipe(takeUntil(this.destory))
      .subscribe(value => {
        if (value) {
          this.paymentAmount.disable();
          this.currency.disable();
        } else {
          this.paymentAmount.enable();
          this.currency.enable();
        }
      });

    this.allSums = this.dialogData.claims.reduce(
      (sum, c) => sum + c.remaining_amount + c.optional_claims.reduce(
        (optionalSum, optionalClaim) => optionalClaim.remaining_amount + optionalSum,
        0,
      ),
      0
    );

    this.currency.patchValue(this.dialogData.claims.find(c => !c.is_paid)?.currency_iso);

    if (this.dialogData!.transition_ids.full_payment === this.data.transitionId) {
      this.fullPayment.patchValue(true);
    }
  }

  ngOnDestroy(): void {
    this.destory.next();
    this.destory.complete();
  }

  readonly submit = async () => {
    if (this.manualPaymentForm.invalid || !this.dialogData) {
      Object.values(this.manualPaymentForm.controls).forEach(control => control.markAsDirty());
      return;
    }

    if (this.isFullPayment) {
      const confirmDialogRef = this.dialog.open(FullPaymentWarningComponent);
      const result = await confirmDialogRef.afterClosed().toPromise();
      if (!result) {
        return;
      }
    }

    const transitionId = this.isFullPayment
      ? this.dialogData.transition_ids.full_payment
      : this.dialogData.transition_ids.partial_payment;

    const closeValue: RunTransition = {
      caseId: this.data.caseId,
      transitionId,
      params: {
        amount: this.paymentAmount.value,
        currency_iso: this.currency.value,
      },
    };
    this.dialogRef.close(closeValue);
  };

  private readonly numberValidator: ValidatorFn = control => {
    const value = control.value;
    if (!value) {
      return null;
    }

    if (typeof value === 'number') {
      return null;
    } else if (typeof value === 'string') {
      control.patchValue(parseInt(value.replace(/\D/g, '') || '0'));
      return null;
    } else {
      return {
        pattern: true,
      };
    }
  };


  private async setDialogData(): Promise<void> {
    try {
      this.loading = true;
      this.dialogData = await this.workflowService.getDialogData({
        caseId: this.data.caseId,
        transitionId: this.data.transitionId,
      }) as DialogData;

    } catch (error: AxiosError | unknown) {
      let message = this.translate.instant('ERROR_MESSAGES.UNKNOWN_ERROR');

      if (error instanceof AxiosError) {
        console.error('Network error while getting dialog data', error.response?.data);
        if (error.response?.data?.message) {
          message = error.response?.data?.message;
        }
      } else {
        console.error('Unknown error while getting dialog data', error);
      }
      this.snackbar.open(
        message,
        'OK',
        {
          duration: 5000,
        },
      );
      this.dialogRef.close();
      return;
    } finally {
      this.loading = false;
    }
  }

  currencyDisplay(currency: string): string {
    return currency;
  }
}
